import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Home, FileText, Info, MessageSquare, Mail, Menu, X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isMobile = useIsMobile();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const navLinks = [
    { name: 'Home', path: '/', icon: <Home className="h-5 w-5" /> },
    { name: 'Services', path: '/services', icon: <FileText className="h-5 w-5" /> },
    { name: 'About', path: '/about', icon: <Info className="h-5 w-5" /> },
    { name: 'Blog', path: '/blog', icon: <MessageSquare className="h-5 w-5" /> },
    { name: 'Contact', path: '/contact', icon: <Mail className="h-5 w-5" /> },
  ];

  return (
    <>
      {/* Desktop and tablet header */}
      <header 
        className={cn(
          'fixed top-0 left-0 right-0 z-50 transition-all duration-300 md:block',
          isScrolled ? 'glass-nav py-3' : 'bg-transparent py-6',
          isMobile && 'hidden' // Hide on mobile
        )}
      >
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between">
            {/* Logo on left */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center">
                <img 
                  src="/fictional.stream-logo.svg" 
                  alt="fictional.stream Logo" 
                  className="h-10 w-10 mr-2" 
                />
                <span className="text-2xl font-bold text-primary">fictional.stream</span>
              </Link>
            </div>

            {/* Navigation links centered */}
            <div className="hidden md:flex flex-grow items-center justify-center">
              <div className="flex space-x-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={cn(
                      'font-medium transition-all duration-300 hover-lift',
                      location.pathname === link.path
                        ? 'text-primary font-semibold'
                        : 'text-foreground/80 hover:text-foreground'
                    )}
                  >
                    {link.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Mobile Menu Button - still needed for iPad or similar sizes */}
            <div className="md:hidden">
              <button
                className="glass-button p-2 rounded-full"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay - slides from top */}
      <div
        className={cn(
          'fixed inset-0 bg-background/95 backdrop-blur-md z-40 md:hidden transition-all duration-300 transform',
          isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
        )}
      >
        <div className="flex flex-col space-y-8 pt-24 px-8">
          {navLinks.map((link) => (
            <div key={link.path} className="flex items-center">
              <span className="mr-3 text-primary">{link.icon}</span>
              <Link
                to={link.path}
                className={cn(
                  'text-xl font-medium transition-all duration-300',
                  location.pathname === link.path
                    ? 'text-primary font-semibold'
                    : 'text-foreground/80'
                )}
              >
                {link.name}
              </Link>
            </div>
          ))}
        </div>
      </div>

      {/* Mobile Top Logo Bar */}
      {isMobile && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 shadow-sm py-2">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-center">
              <Link to="/" className="flex items-center">
                <img 
                  src="/fictional.stream-logo.svg" 
                  alt="fictional.stream Logo" 
                  className="h-8 w-8 mr-2" 
                />
                <span className="text-xl font-bold text-primary">fictional.stream</span>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation Bar - inspired by webicrew.com */}
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg md:hidden">
          <div className="flex justify-around items-center py-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="flex flex-col items-center p-2"
              >
                <span className={cn(
                  location.pathname === link.path ? 'text-primary' : 'text-gray-500',
                  'hover:text-primary transition-colors duration-300'
                )}>
                  {link.icon}
                </span>
                <span className={cn(
                  'text-xs mt-1',
                  location.pathname === link.path ? 'text-primary font-medium' : 'text-gray-600'
                )}>
                  {link.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
